import { type ChangeEvent, type FormEvent, useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PERMISSIONS } from '@lms/shared-types';
import { Loader, useToast } from '@lms/ui';
import { HubBackLink } from '@/components/hub/HubBackLink';
import { usePermissions } from '@/hooks/usePermissions';
import { api, apiFormData } from '@/lib/api';
import { roleActionErrorToast, siteAssetSuccessToast } from '@/lib/user-feedback';

interface SiteBrandingAsset {
  mediaFileId: string;
  url: string;
  filename: string;
  mimeType: string;
}

interface SiteBranding {
  logo: SiteBrandingAsset | null;
  legalRepresentativeSignature: SiteBrandingAsset | null;
  drivingSchoolName: string | null;
  drivingSchoolCommune: string | null;
  drivingSchoolMunicipality: string | null;
  updatedAt: string | null;
}

interface DrivingSchoolValues {
  name: string;
  commune: string;
  municipality: string;
}

const SCHOOL_FIELD_CLASS =
  'mt-1 w-full rounded-lg border border-dash-outline-variant bg-white px-4 py-3 text-base text-dash-on-surface outline-none focus:border-dash-secondary focus:ring-2 focus:ring-dash-secondary/20';

function DrivingSchoolCard({
  values,
  saving,
  canSave,
  onSave,
}: {
  values: DrivingSchoolValues;
  saving: boolean;
  canSave: boolean;
  onSave: (value: DrivingSchoolValues) => void;
}) {
  const [name, setName] = useState(values.name);
  const [commune, setCommune] = useState(values.commune);
  const [municipality, setMunicipality] = useState(values.municipality);
  useEffect(() => {
    setName(values.name);
    setCommune(values.commune);
    setMunicipality(values.municipality);
  }, [values.name, values.commune, values.municipality]);
  const dirty =
    name.trim() !== values.name.trim() ||
    commune.trim() !== values.commune.trim() ||
    municipality.trim() !== values.municipality.trim();

  function submit(event: FormEvent) {
    event.preventDefault();
    if (!canSave || !dirty || saving) return;
    onSave({ name: name.trim(), commune: commune.trim(), municipality: municipality.trim() });
  }

  return (
    <section className="lms-appearance__card lms-appearance__card--wide">
      <div className="lms-appearance__card-icon" aria-hidden="true">
        <span className="material-symbols-outlined">school</span>
      </div>
      <form className="min-w-0 flex-1" onSubmit={submit}>
        <h2 className="lms-appearance__card-title">Escuela de conductores</h2>
        <p className="lms-appearance__card-text">
          Nombre, comuna y municipio de la escuela. Se usan como datos institucionales del sitio.
        </p>
        <label className="mt-4 block">
          <span className="text-sm font-medium text-dash-on-surface-variant">Nombre</span>
          <input
            value={name}
            maxLength={160}
            readOnly={!canSave}
            placeholder="Nombre de la escuela de conductores"
            onChange={(event) => setName(event.target.value)}
            className={SCHOOL_FIELD_CLASS}
          />
        </label>
        <div className="mt-4 grid gap-4 md:grid-cols-2">
          <label className="block">
            <span className="text-sm font-medium text-dash-on-surface-variant">Comuna de la escuela</span>
            <input
              value={commune}
              maxLength={160}
              readOnly={!canSave}
              placeholder="Comuna de la escuela"
              onChange={(event) => setCommune(event.target.value)}
              className={SCHOOL_FIELD_CLASS}
            />
          </label>
          <label className="block">
            <span className="text-sm font-medium text-dash-on-surface-variant">Municipio</span>
            <input
              value={municipality}
              maxLength={160}
              readOnly={!canSave}
              placeholder="Municipio"
              onChange={(event) => setMunicipality(event.target.value)}
              className={SCHOOL_FIELD_CLASS}
            />
          </label>
        </div>
        {canSave ? (
          <div className="lms-appearance__actions">
            <button type="submit" className="lms-appearance__btn lms-appearance__btn--primary" disabled={!dirty || saving}>
              {saving ? 'Guardando…' : 'Guardar'}
            </button>
          </div>
        ) : null}
      </form>
    </section>
  );
}

function AssetPreview({ src, alt }: { src: string; alt: string }) {
  return (
    <div className="lms-appearance__preview">
      <img src={src} alt={alt} className="lms-appearance__preview-image" />
    </div>
  );
}

function AssetCard({
  title,
  description,
  icon,
  previewSrc,
  previewAlt,
  emptyLabel,
  uploading,
  canManage,
  onUpload,
  onRemove,
  hasAsset,
}: {
  title: string;
  description: string;
  icon: string;
  previewSrc?: string;
  previewAlt: string;
  emptyLabel: string;
  uploading: boolean;
  canManage: boolean;
  onUpload: (file: File) => void;
  onRemove: () => void;
  hasAsset: boolean;
}) {
  const inputRef = useRef<HTMLInputElement>(null);

  function handleChange(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (file) onUpload(file);
    e.target.value = '';
  }

  return (
    <section className="lms-appearance__card">
      <div className="lms-appearance__card-icon" aria-hidden="true">
        <span className="material-symbols-outlined">{icon}</span>
      </div>
      <div className="min-w-0 flex-1">
        <h2 className="lms-appearance__card-title">{title}</h2>
        <p className="lms-appearance__card-text">{description}</p>

        {previewSrc ? (
          <AssetPreview src={previewSrc} alt={previewAlt} />
        ) : (
          <div className="lms-appearance__empty">{emptyLabel}</div>
        )}

        {canManage ? (
          <div className="lms-appearance__actions">
            <label className="lms-appearance__btn lms-appearance__btn--primary">
              <span className="material-symbols-outlined" aria-hidden="true">
                upload
              </span>
              {uploading ? 'Subiendo…' : hasAsset ? 'Reemplazar' : 'Subir archivo'}
              <input
                ref={inputRef}
                type="file"
                accept="image/png,image/jpeg,image/webp,image/gif"
                className="sr-only"
                disabled={uploading}
                onChange={handleChange}
              />
            </label>
            {hasAsset && (
              <button
                type="button"
                className="lms-appearance__btn lms-appearance__btn--danger"
                disabled={uploading}
                onClick={onRemove}
              >
                <span className="material-symbols-outlined" aria-hidden="true">
                  delete
                </span>
                Quitar
              </button>
            )}
          </div>
        ) : null}
      </div>
    </section>
  );
}

export function AparienciaPage() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { hasPermission } = usePermissions();
  const canAdminister = hasPermission(PERMISSIONS.APARIENCIA_ADMINISTRAR);

  const { data, isLoading, error } = useQuery({
    queryKey: ['site-branding'],
    queryFn: () => api<SiteBranding>('/site/branding'),
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['site-branding'] });

  const uploadLogo = useMutation({
    mutationFn: ({ file }: { file: File; replacing: boolean }) => {
      const form = new FormData();
      form.append('file', file);
      return apiFormData<SiteBranding>('/site/branding/logo', form, { method: 'POST' });
    },
    onSuccess: (_data, variables) => {
      invalidate();
      toast(siteAssetSuccessToast(variables.replacing ? 'edit' : 'add'));
    },
    onError: (error, variables) => {
      toast(
        roleActionErrorToast(
          error,
          variables.replacing ? 'No se pudo editar el asset' : 'No se pudo agregar el asset',
        ),
      );
    },
  });

  const uploadSignature = useMutation({
    mutationFn: ({ file }: { file: File; replacing: boolean }) => {
      const form = new FormData();
      form.append('file', file);
      return apiFormData<SiteBranding>('/site/branding/legal-signature', form, { method: 'POST' });
    },
    onSuccess: (_data, variables) => {
      invalidate();
      toast(siteAssetSuccessToast(variables.replacing ? 'edit' : 'add'));
    },
    onError: (error, variables) => {
      toast(
        roleActionErrorToast(
          error,
          variables.replacing ? 'No se pudo editar el asset' : 'No se pudo agregar el asset',
        ),
      );
    },
  });

  const clearLogo = useMutation({
    mutationFn: () => api<SiteBranding>('/site/branding/logo', { method: 'DELETE' }),
    onSuccess: () => {
      invalidate();
      toast(siteAssetSuccessToast('delete'));
    },
    onError: (error) => {
      toast(roleActionErrorToast(error, 'No se pudo eliminar el asset'));
    },
  });

  const saveSchool = useMutation({
    mutationFn: (values: DrivingSchoolValues) =>
      api<SiteBranding>('/site/branding', {
        method: 'PATCH',
        body: {
          drivingSchoolName: values.name,
          drivingSchoolCommune: values.commune,
          drivingSchoolMunicipality: values.municipality,
        },
      }),
    onSuccess: () => {
      invalidate();
      toast(siteAssetSuccessToast('edit'));
    },
    onError: (error) => {
      toast(roleActionErrorToast(error, 'No se pudieron guardar los datos de la escuela'));
    },
  });

  const clearSignature = useMutation({
    mutationFn: () => api<SiteBranding>('/site/branding/legal-signature', { method: 'DELETE' }),
    onSuccess: () => {
      invalidate();
      toast(siteAssetSuccessToast('delete'));
    },
    onError: (error) => {
      toast(roleActionErrorToast(error, 'No se pudo eliminar el asset'));
    },
  });

  const logoPreview = data?.logo ? '/api/site/branding/logo/content' : undefined;
  const signaturePreview = data?.legalRepresentativeSignature
    ? '/api/site/branding/legal-signature/content'
    : undefined;

  return (
    <div className="lms-appearance lms-site-admin-hub-page font-sans">
      <header className="mb-6 flex items-start gap-4">
        <HubBackLink to="/administracion?tab=design" label="Volver a Administración" />
        <div className="min-w-0">
          <h1 className="text-3xl font-semibold text-dash-primary md:text-4xl">Assets del sitio</h1>
          <p className="mt-1 max-w-2xl text-dash-on-surface-variant">
            Logo, firma del representante legal, nombre, comuna y municipio de la escuela de conductores.
          </p>
        </div>
      </header>

      {error && (
        <p className="lms-appearance__error" role="alert">
          {(error as Error).message}
        </p>
      )}

      {isLoading ? (
        <div className="lms-appearance__loading">
          <Loader />
        </div>
      ) : (
        <div className="lms-appearance__panel">
          <DrivingSchoolCard
            values={{
              name: data?.drivingSchoolName ?? '',
              commune: data?.drivingSchoolCommune ?? '',
              municipality: data?.drivingSchoolMunicipality ?? '',
            }}
            saving={saveSchool.isPending}
            canSave={canAdminister}
            onSave={(value) => saveSchool.mutate(value)}
          />

          <AssetCard
            title="Logo del sitio"
            description="Se usa en plantillas de contrato con la variable {{sistema.logo}} y en otras comunicaciones oficiales."
            icon="image"
            previewSrc={logoPreview}
            previewAlt={data?.logo?.filename ?? 'Logo del sitio'}
            emptyLabel="Aún no hay logo configurado."
            uploading={uploadLogo.isPending || clearLogo.isPending}
            canManage={canAdminister}
            hasAsset={Boolean(data?.logo)}
            onUpload={(file) => uploadLogo.mutate({ file, replacing: Boolean(data?.logo) })}
            onRemove={() => clearLogo.mutate()}
          />

          <AssetCard
            title="Firma del representante legal"
            description="Imagen de la firma escaneada. En contratos usa {{sistema.firma_representante}} en la columna del prestador."
            icon="draw"
            previewSrc={signaturePreview}
            previewAlt={data?.legalRepresentativeSignature?.filename ?? 'Firma del representante legal'}
            emptyLabel="Aún no hay firma configurada."
            uploading={uploadSignature.isPending || clearSignature.isPending}
            canManage={canAdminister}
            hasAsset={Boolean(data?.legalRepresentativeSignature)}
            onUpload={(file) =>
              uploadSignature.mutate({ file, replacing: Boolean(data?.legalRepresentativeSignature) })
            }
            onRemove={() => clearSignature.mutate()}
          />
        </div>
      )}
    </div>
  );
}
